import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PropTypes } from 'prop-types';
import { Select } from 'antd';
import { getIdMap } from 'ontraccr-common/lib/Common';

import { ConflictLabel, useConflictTagRender } from './FieldSchedule/ConflictSelectRenderers';
import { formatEquipmentLabelFromCompanySettings } from '../equipment/equipment.helpers';
import { getShiftConflictsByKey } from './scheduleHelpers';

export default function ScheduleEquipmentSelector({
  value,
  onChange,
  equipmentOptions,
  dateRange,
  shiftId,
  settings,
}) {
  const shiftMap = useSelector((state) => state.schedule.shiftMap);
  const categories = useSelector((state) => state.schedule.categories);
  const projects = useSelector((state) => state.projects.projects);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const categoryMap = useMemo(() => getIdMap(categories), [categories]);

  const shiftIdsByEquipmentId = useMemo(() => {
    const equipmentIdSet = new Set(equipmentOptions.map((item) => item.id));
    const result = {};
    Object.values(shiftMap).forEach((shift) => {
      shift?.equipment?.forEach((equipmentId) => {
        if (!equipmentIdSet.has(equipmentId)) return;
        if (!result[equipmentId]) result[equipmentId] = [];
        result[equipmentId].push(shift.id);
      });
    });
    return result;
  }, [equipmentOptions, shiftMap]);

  const conflictsByEquipmentId = useMemo(() => getShiftConflictsByKey({
    dateRange,
    excludeShiftId: shiftId,
    shiftIdsByKey: shiftIdsByEquipmentId,
    shiftMap,
    projectMap,
    categoryMap,
  }), [dateRange, shiftId, shiftIdsByEquipmentId, shiftMap, projectMap, categoryMap]);

  const tagRender = useConflictTagRender(conflictsByEquipmentId);

  return (
    <Select
      mode="multiple"
      value={value}
      onChange={onChange}
      allowClear
      placeholder="Equipment"
      optionLabelProp="label"
      optionFilterProp="label"
      tagRender={tagRender}
    >
      {equipmentOptions.map((item) => {
        const label = formatEquipmentLabelFromCompanySettings({
          name: item.name,
          code: item.code,
          settings,
        });

        return (
          <Select.Option key={item.id} value={item.id} label={label}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <ConflictLabel label={label} conflicts={conflictsByEquipmentId[item.id]} />
            </div>
          </Select.Option>
        );
      })}
    </Select>
  );
}

ScheduleEquipmentSelector.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  equipmentOptions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    code: PropTypes.string,
  })),
  dateRange: PropTypes.arrayOf(PropTypes.number),
  shiftId: PropTypes.string,
  settings: PropTypes.shape({}),
};

ScheduleEquipmentSelector.defaultProps = {
  value: [],
  onChange: () => {},
  equipmentOptions: [],
  dateRange: [],
  shiftId: null,
  settings: {},
};
